import { useEffect, useState } from "react";
import { api, fmtEur, fmtMese, formatErr, currentCompetenzaMese, canWrite } from "@/lib/api";
import { MovTable, AddRigaDialog, NumeroConteggio } from "@/components/ConteggioRighe";
import { SaldoPrecedente } from "@/components/SaldoPrecedente";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuth } from "@/contexts/AuthContext";
import { ExportPdfButton } from "@/components/ExportPdfButton";
import { MonthPicker } from "@/components/MonthPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download, FileText, Lock, Unlock, Eye, Search } from "lucide-react";
import { toast } from "sonner";

export default function Conteggi() {
  const { company } = useCompany();
  const { user } = useAuth();
  const [mese, setMese] = useState(currentCompetenzaMese());
  const [padroncini, setPadroncini] = useState([]);
  const [sel, setSel] = useState(null);
  const [conteggio, setConteggio] = useState(null);
  const [layout, setLayout] = useState("moderno");
  const [addTipo, setAddTipo] = useState(null);
  const [editRiga, setEditRiga] = useState(null);
  const righeEdit = canWrite(user, "movimenti") && !conteggio?.locked;
  const delRiga = async (m) => {
    if (!window.confirm(`Eliminare "${m.descrizione}"?`)) return;
    try { await api.delete(`/movimenti/${m.id}`); toast.success("Riga eliminata"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const salvaContributi = async (v) => {
    try {
      await api.put(`/conteggi/${conteggio.padroncino_id}/contributi`, { mese: conteggio.mese, importo: parseFloat(v || 0) });
      toast.success("Contributi salvati (solo nel conteggio)"); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const [chiusura, setChiusura] = useState(null);
  const [mezzo, setMezzo] = useState("");
  const [q, setQ] = useState("");
  const [targhe, setTarghe] = useState({});

  useEffect(() => {
    api.get("/padroncini", { params: { company } }).then(r => setPadroncini(r.data));
    api.get("/veicoli", { params: { company } }).then(r => {
      const m = {};
      r.data.forEach(v => { if (v.padroncino_id) m[v.padroncino_id] = `${m[v.padroncino_id] || ""} ${v.targa}`; });
      setTarghe(m);
    });
  }, [company]);
  const term = q.trim().toLowerCase();
  const filtered = !term ? padroncini : padroncini.filter(p =>
    `${p.codice} ${p.nome} ${p.cognome || ""} ${targhe[p.id] || ""}`.toLowerCase().includes(term));
  useEffect(() => { if (sel) load(); /* eslint-disable-line */ }, [sel, mese, mezzo]);
  useEffect(() => {
    if (company === "all") { setChiusura(null); return; }
    api.get("/chiusure", { params: { company } }).then(r => {
      const c = r.data.find(x => x.mese === mese); setChiusura(c || null);
    });
  }, [company, mese]);

  const load = async () => {
    try {
      const { data } = await api.get(`/conteggi/${sel}`, { params: { mese, ...(mezzo ? { mezzo } : {}) } });
      setConteggio(data); setLayout(data.pdf_layout);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const scarica = async (params, name, pid = sel) => {
    const r = await api.get(`/conteggi/${pid}/pdf`, { params: { mese, layout, ...params }, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `conteggio_${conteggio.padroncino_codice}_${mese}${name}_${layout}.pdf`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };
  const targaFile = (m) => "_" + m.label.replace(/\s+/g, "");
  const downloadPdf = async (mode = "uno") => {
    try {
      if (mode === "tutti") await scarica({ tutti: true }, "_completo");
      else if (mode === "separati") {
        await scarica({}, "_generale");
        for (const m of conteggio.mezzi) await scarica({ mezzo: m.id }, targaFile(m));
        for (const f of conteggio.collegati || []) await scarica({}, targaFile(f), f.id);
      } else {
        const m = conteggio.mezzi.find((x) => x.id === mezzo);
        await scarica(mezzo ? { mezzo } : {}, m ? targaFile(m) : conteggio.multi_mezzo || conteggio.collegati?.length ? "_generale" : "");
      }
    } catch (e) { toast.error("Errore download PDF"); }
  };

  const chiudi = async () => {
    if (company === "all") return toast.error("Seleziona un'azienda specifica");
    if (!window.confirm(`Confermi la chiusura di ${fmtMese(mese)} per ${company}?`)) return;
    try { await api.post("/chiusure", { company, mese }); toast.success("Mese chiuso"); 
      setChiusura({ locked: true }); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const riapri = async () => {
    try { await api.post("/chiusure/riapri", { company, mese }); toast.success("Mese riaperto");
      setChiusura({ locked: false }); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-heading font-black">Conteggi mensili</h1>
        <div className="flex items-center gap-2">
          <Label className="text-xs font-heading uppercase">Mese</Label>
          <MonthPicker value={mese} onChange={setMese} testId="conteggi-mese-input" />
          <ExportPdfButton resource="conteggi" params={{ company, mese }} label="PDF tutti i conteggi" />
          {user?.role === "admin" && company !== "all" && (
            chiusura?.locked ? (
              <Button size="sm" variant="outline" className="h-8 gap-1" onClick={riapri} data-testid="riapri-mese-button">
                <Unlock className="w-3.5 h-3.5" />Riapri mese
              </Button>
            ) : (
              <Button size="sm" className="h-8 gap-1 bg-green-600 hover:bg-green-700" onClick={chiudi} data-testid="chiudi-mese-button">
                <Lock className="w-3.5 h-3.5" />Chiudi {fmtMese(mese)}
              </Button>
            )
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Lista padroncini */}
        <Card className="rounded-md p-2 lg:col-span-1 max-h-[75vh] overflow-auto">
          <div className="text-xs font-heading uppercase tracking-wider text-muted-foreground px-2 py-1">Padroncini</div>
          <div className="relative px-1 pb-2 sticky top-0 bg-card z-10">
            <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca padroncino o targa..."
                   className="h-8 pl-7" data-testid="conteggi-search-padroncini" />
          </div>
          {filtered.map((p) => (

            <button key={p.id} onClick={() => { setMezzo(""); setSel(p.id); }}
                    className={`w-full text-left px-2 py-1.5 text-sm rounded-sm hover:bg-muted ${sel === p.id ? "bg-primary text-primary-foreground hover:bg-primary" : ""}`}
                    data-testid={`conteggi-select-${p.id}`}>
              <div className="font-heading font-bold" data-testid={`conteggi-nome-${p.id}`}>{p.nome} {p.cognome}</div>
              <div className="text-xs opacity-80">N. {p.codice}</div>
              {targhe[p.id] && <div className="text-[10px] font-mono opacity-70">{targhe[p.id].trim()}</div>}
              {p.conteggio_generale_id && <div className="text-[10px] opacity-70">↳ collegato al generale</div>}
            </button>
          ))}
          {filtered.length === 0 && <div className="text-sm text-muted-foreground p-3" data-testid="conteggi-search-empty">{padroncini.length ? "Nessun risultato." : "Nessun padroncino."}</div>}
        </Card>

        <div className="lg:col-span-3 space-y-3">
          {!conteggio ? (
            <Card className="p-10 rounded-md text-center text-muted-foreground">Seleziona un padroncino per visualizzare il conteggio</Card>
          ) : (
            <>
              <Card className="p-4 rounded-md">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-3">
                      <div className="font-heading font-black text-xl">{conteggio.padroncino_nome}</div>
                      <NumeroConteggio key={`${conteggio.padroncino_id}-${mezzo}-${conteggio.numero}`} c={conteggio} mezzo={mezzo} onSaved={load} disabled={!righeEdit} />
                    </div>
                    <div className="text-sm text-muted-foreground">{conteggio.azienda_name} · Conteggio {fmtMese(conteggio.mese)}{conteggio.targa ? ` · ${conteggio.targa}` : ""}{conteggio.autista ? ` · Autista ${conteggio.autista}` : ""}</div>
                    {conteggio.generale && <button type="button" className="text-xs underline text-primary" onClick={() => { setMezzo(""); setSel(conteggio.generale.id); }} data-testid="conteggi-vai-generale">
                      Collegato al conteggio generale di {conteggio.generale.nome} (riparte da zero ogni mese) → apri il generale</button>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={layout} onValueChange={setLayout}>
                      <SelectTrigger className="h-8 w-60" data-testid="conteggi-layout-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="moderno">Layout Moderno</SelectItem>
                        <SelectItem value="classico">Layout Classico Golmar</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" className="h-8 gap-1" onClick={() => downloadPdf()} data-testid="conteggi-download-pdf">
                      <Download className="w-4 h-4" />PDF{conteggio.multi_mezzo || conteggio.collegati?.length ? " questo" : ""}
                    </Button>
                    {(conteggio.multi_mezzo || conteggio.collegati?.length > 0) && <>
                      <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => downloadPdf("tutti")} data-testid="conteggi-download-tutti">
                        <Download className="w-4 h-4" />Un PDF con tutto
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => downloadPdf("separati")} data-testid="conteggi-download-separati">
                        <Download className="w-4 h-4" />PDF separati
                      </Button>
                    </>}
                  </div>
                </div>
                {conteggio.multi_mezzo && (
                  <Tabs value={mezzo || "generale"} onValueChange={(v) => setMezzo(v === "generale" ? "" : v)} className="mb-3">
                    <TabsList className="flex-wrap h-auto">
                      <TabsTrigger value="generale" data-testid="conteggi-tab-generale">Generale</TabsTrigger>
                      {conteggio.mezzi.map((m) => <TabsTrigger key={m.id} value={m.id} data-testid={`conteggi-tab-mezzo-${m.id}`}>{m.label}</TabsTrigger>)}
                    </TabsList>
                  </Tabs>
                )}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <SaldoPrecedente c={conteggio} editable={righeEdit} onSaved={load} />
                  {conteggio.saldi_mezzi?.length > 0 && <Stat label="Saldo mezzi" v={conteggio.totale_mezzi} />}
                  <Stat label="Entrate" v={conteggio.totale_entrate} positive />
                  <Stat label="Spese" v={-conteggio.totale_spese} negative />
                  <Stat label="Pagamenti" v={-conteggio.totale_pagamenti} />
                  <Stat label="Saldo finale" v={conteggio.saldo_finale} highlight />
                </div>
                {conteggio.saldi_mezzi?.length > 0 && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="conteggi-saldi-mezzi">
                    {conteggio.saldi_mezzi.map((s) => (
                      <button key={s.id} type="button" onClick={() => { if (s.padroncino_id) { setMezzo(""); setSel(s.padroncino_id); } else setMezzo(s.id); }} className="flex justify-between border rounded-sm px-3 py-2 text-sm hover:bg-muted" data-testid={`conteggi-saldo-mezzo-${s.id}`}>
                        <span className="font-mono">SALDO - {s.label}</span><span className={`font-mono font-bold ${s.saldo < 0 ? "text-destructive" : ""}`}>{fmtEur(s.saldo)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </Card>

              {/* Side-by-side PDF preview */}
              <Card className="p-3 rounded-md">
                <div className="font-heading font-bold mb-2 flex items-center gap-2"><Eye className="w-4 h-4" />Anteprima PDF (Classico vs Moderno)</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><div className="text-xs text-muted-foreground mb-1 font-heading uppercase">Classico Golmar</div>
                    <PdfFrame title="classico" path={`/conteggi/${sel}/preview?mese=${mese}&layout=classico${mezzo ? `&mezzo=${mezzo}` : ""}&v=${conteggio.saldo_finale}`}
                            className="w-full h-[520px] border rounded-sm bg-white"
                            data-testid="conteggi-preview-classico" />
                  </div>
                  <div><div className="text-xs text-muted-foreground mb-1 font-heading uppercase">Moderno</div>
                    <PdfFrame title="moderno" path={`/conteggi/${sel}/preview?mese=${mese}&layout=moderno${mezzo ? `&mezzo=${mezzo}` : ""}&v=${conteggio.saldo_finale}`}
                            className="w-full h-[520px] border rounded-sm bg-white"
                            data-testid="conteggi-preview-moderno" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 rounded-md">
                <div className="font-heading font-bold mb-2">Dettaglio movimenti</div>
                                <MovTable title="Spese" rows={conteggio.spese} neg tipo="spesa" writable={righeEdit} onAdd={() => setAddTipo("spesa")} onDelete={delRiga} onEdit={setEditRiga} onReload={load} onContributi={salvaContributi} />
                <MovTable title="Entrate" rows={conteggio.entrate} tipo="entrata" writable={righeEdit} onAdd={() => setAddTipo("entrata")} onDelete={delRiga} onEdit={setEditRiga} onReload={load} />
                <MovTable title="Pagamenti" rows={conteggio.pagamenti} neg tipo="pagamento" writable={righeEdit} onAdd={() => setAddTipo("pagamento")} onDelete={delRiga} onEdit={setEditRiga} onReload={load} />
                {addTipo && <AddRigaDialog tipo={addTipo} c={conteggio} mezzo={mezzo} onClose={() => setAddTipo(null)} onSaved={() => { setAddTipo(null); load(); }} />}
                {editRiga && <AddRigaDialog riga={editRiga} tipo={editRiga.tipo} c={conteggio} mezzo={mezzo} onClose={() => setEditRiga(null)} onSaved={() => { setEditRiga(null); load(); }} />}
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PdfFrame({ title, path, className, ...rest }) {
  const [pages, setPages] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    setPages(null); setErr(false);
    api.get(path).then((r) => setPages(r.data.pages)).catch(() => setErr(true));
  }, [path]);
  if (err) return <div className="h-[520px] border rounded-sm flex items-center justify-center text-sm text-destructive">Anteprima non disponibile</div>;
  if (!pages) return <div className="h-[520px] border rounded-sm flex items-center justify-center text-sm text-muted-foreground">Caricamento…</div>;
  return (
    <div className={`${className} overflow-auto space-y-2 p-2 bg-slate-200 dark:bg-slate-800`} {...rest}>
      {pages.map((src, i) => (
        <img key={i} src={src} alt={`${title} pagina ${i + 1}`} className="w-full shadow-sm bg-white" data-testid={`preview-${title}-page-${i + 1}`} />
      ))}
    </div>
  );
}

function Stat({ label, v, positive, negative, highlight }) {
  const color = highlight ? (v >= 0 ? "text-primary" : "text-destructive") :
                positive ? "text-green-600" : negative ? "text-destructive" : "text-foreground";
  return (
    <div className="border rounded-sm p-2.5">
      <div className="text-xs font-heading uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-mono font-bold text-base ${color}`}>{fmtEur(v)}</div>
    </div>
  );
}

